import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, CurrencyDollar, TrendUp, Clock, ArrowsClockwise } from "@phosphor-icons/react";

const fmt = (v) => `$${Number(Math.round(v || 0)).toLocaleString()}`;

// Simple, honest personal scoreboard — no prescriptive "buy/pass" calls.
export default function FlipScoreboard() {
  const [wins, setWins] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portfolio/wins")
      .then(({ data }) => setWins(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  if (!wins || !wins.count) {
    return (
      <div className="border border-dashed border-[#D6D3CD] bg-[#FBFAF6] rounded-xl p-5 mb-8" data-testid="flip-scoreboard-empty">
        <div className="flex items-center gap-2 mb-1">
          <Trophy size={18} weight="fill" className="text-[#B47A3C]" />
          <span className="label-caps">Your flips</span>
        </div>
        <p className="text-[#57534E] text-sm">
          Log your first sale (or scout a find and sell it) and your flip scoreboard starts here — your returns, at a glance.
        </p>
      </div>
    );
  }

  const tiles = [
    { icon: Trophy, label: "Flips", value: `${wins.count}`, testid: "flip-count" },
    { icon: TrendUp, label: "Avg return", value: wins.avg_multiple != null ? `${wins.avg_multiple}×` : "—", testid: "flip-multiple" },
    { icon: CurrencyDollar, label: "Total profit", value: wins.total_profit != null ? fmt(wins.total_profit) : "—", testid: "flip-profit" },
    { icon: Clock, label: "Avg days to sell", value: wins.avg_days_to_sale != null ? `${wins.avg_days_to_sale}d` : "—", testid: "flip-days" },
    ...(wins.sell_through_pct != null ? [{ icon: ArrowsClockwise, label: "Sell-through", value: `${wins.sell_through_pct}%`, testid: "flip-sellthrough" }] : []),
  ];

  return (
    <div className="bg-[#0D1B2A] text-[#FDFBF7] rounded-xl p-5 sm:p-6 mb-8" data-testid="flip-scoreboard">
      <div className="flex items-center gap-2 mb-4">
        <Trophy size={18} weight="fill" className="text-[#B47A3C]" />
        <span className="label-caps" style={{ color: "#E5E5E5" }}>Your flips</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {tiles.map((s) => (
          <div key={s.label} data-testid={s.testid} className="border border-white/10 bg-white/5 rounded-lg p-3">
            <s.icon size={16} weight="fill" className="text-[#B47A3C] mb-1.5" />
            <p className="font-serif-display text-2xl font-bold leading-none">{s.value}</p>
            <p className="label-caps mt-1.5" style={{ color: "#B8B8B8" }}>{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
